import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { publicQueue } from "../../api";
import { t, getDir } from "../../i18n";

const POLL_INTERVAL = 10000;

// ── Mode-aware rendering config ───────────────────────────────────────────────
const MODE_CONFIG = {
  none: {
    fontSize: "72px", highContrast: false, audioAlert: false, visualFlash: false,
  },
  simple: {
    fontSize: "96px", highContrast: true, audioAlert: false, visualFlash: false,
  },
  hearing: {
    fontSize: "88px", highContrast: true, audioAlert: false, visualFlash: true,
  },
  visual: {
    fontSize: "72px", highContrast: true, audioAlert: true, visualFlash: false,
  },
  mobility: {
    fontSize: "72px", highContrast: false, audioAlert: false, visualFlash: false,
  },
  communication: {
    fontSize: "72px", highContrast: false, audioAlert: false, visualFlash: false,
  },
};

export default function TrackingPage() {
  const { token }                    = useParams();
  const [data,    setData]           = useState(null);
  const [loading, setLoading]        = useState(true);
  const [error,   setError]          = useState(null);
  const [lastUpdated, setLastUpdated]= useState(null);
  const [flash,   setFlash]          = useState(false);
  const timerRef   = useRef(null);
  const mountedRef = useRef(true);
  const prevStatus = useRef(null);

  // Language is driven entirely by the ticket itself (set at kiosk time) —
  // the patient never has to pick a language twice.
  const lang = data?.language || "en";

  const poll = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const { data: res } = await publicQueue.trackTicket(token);
      if (mountedRef.current) {
        // Detect transition to "serving" for visual/hearing flash + audio
        if (prevStatus.current !== "serving" && res.status === "serving") {
          const mode = res.accessibility_mode || "none";
          if (MODE_CONFIG[mode]?.visualFlash) triggerFlash();
          if (MODE_CONFIG[mode]?.audioAlert)  speakAlert(res);
        }
        prevStatus.current = res.status;
        setData(res);
        setError(null);
        setLastUpdated(new Date());
      }
    } catch (e) {
      if (mountedRef.current) {
        const l = data?.language || "en";
        setError(e.response?.status === 404
          ? t(l, "error.notFound")
          : t(l, "error.loadFailed"));
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    mountedRef.current = true;
    poll(true);
    timerRef.current = setInterval(() => poll(false), POLL_INTERVAL);
    return () => {
      mountedRef.current = false;
      clearInterval(timerRef.current);
    };
  }, [poll]);

  useEffect(() => {
    if (data?.status === "completed" || data?.status === "abandoned" || data?.status === "expired") {
      clearInterval(timerRef.current);
    }
  }, [data?.status]);

  const triggerFlash = () => {
    setFlash(true);
    setTimeout(() => setFlash(false), 3000);
  };

  const speakAlert = (ticket) => {
    if (!window.speechSynthesis) return;
    const ttsLang = ticket.language || "en";
    const text = ttsLang === "tw"
      ? `Ticket number ${ticket.ticket_number}. Kɔ ${ticket.current_counter || ticket.current_station} wɔ sɔn.`
      : ttsLang === "fr"
      ? `Votre numéro ${ticket.ticket_number} est appelé maintenant. Veuillez vous rendre à ${ticket.current_counter || ticket.current_station}.`
      : `Your number ${ticket.ticket_number} is now being called. Please go to ${ticket.current_counter || ticket.current_station}.`;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate  = 0.9;
    utter.volume = 1;
    utter.lang  = ttsLang === "tw" ? "ak-GH" : ttsLang === "fr" ? "fr-FR" : "en-GH";
    window.speechSynthesis.speak(utter);
  };

  if (loading && !data) return <Shell flash={false} lang={lang}><LoadingView lang={lang} /></Shell>;
  if (error)           return <Shell flash={false} lang={lang}><ErrorView lang={lang} message={error} /></Shell>;
  if (!data)           return null;

  const mode   = data.accessibility_mode || "none";
  const modeCfg = MODE_CONFIG[mode] || MODE_CONFIG.none;
  const STATUS = getStatusConfig(data.status, lang);

  return (
    <Shell flash={flash && data.status === "serving"} mode={mode} lang={lang}>
      {/* Hearing/Visual: giant "YOUR TURN" banner when serving */}
      {(mode === "hearing" || mode === "visual") && data.status === "serving" && (
        <div style={T.urgentBanner}>
          <div style={T.urgentTitle}>{t(lang, "urgentTitle")}</div>
          <div style={T.urgentSub}>
            {t(lang, "urgentSub")}{" "}
            <strong>{data.current_counter || data.current_station}</strong>
          </div>
        </div>
      )}

      {/* Simple mode: extra-large card */}
      <div style={{ ...T.card, ...(modeCfg.highContrast ? T.cardHighContrast : {}) }}>
        {/* Header */}
        <div style={T.cardHeader}>
          <div style={T.poweredBy}>{t(lang, "poweredBy")}</div>
          {lastUpdated && (
            <div style={T.lastUpdated}>{t(lang, "updated", { time: formatTime(lastUpdated) })}</div>
          )}
        </div>

        {/* Accessibility mode banner */}
        {mode !== "none" && <AccessModeBanner mode={mode} lang={lang} />}

        {/* Status banner */}
        <div style={{ ...T.statusBanner, background: STATUS.bg, border: `1px solid ${STATUS.border}` }}>
          <span style={{ fontSize: "28px" }}>{STATUS.icon}</span>
          <div>
            <div style={{ ...T.statusLabel, color: STATUS.color }}>{STATUS.label}</div>
            <div style={{ fontSize: "13px", color: "#64748B", marginTop: "2px" }}>{STATUS.desc}</div>
          </div>
        </div>

        {/* Ticket number */}
        <div style={T.ticketBlock}>
          <div style={T.ticketNumLabel}>{t(lang, "yourQueueNumber")}</div>
          <div style={{ ...T.ticketNum, fontSize: modeCfg.fontSize }}>
            {data.ticket_number}
          </div>
          <div style={T.patientName}>{data.patient_name}</div>
          {data.priority !== "normal" && (
            <div style={{
              ...T.priorityChip,
              background: data.priority === "emergency" ? "rgba(239,68,68,0.1)" : "rgba(234,179,8,0.1)",
              color:      data.priority === "emergency" ? "#EF4444" : "#EAB308",
            }}>
              {data.priority === "emergency" ? t(lang, "priority.emergency") : t(lang, "priority.priority")}
            </div>
          )}
        </div>

        {/* Position + Wait */}
        {(data.status === "waiting" || data.status === "serving") && (
          <div style={T.infoRow}>
            {data.position != null && data.status === "waiting" && (
              <InfoBox icon="🎯" label={t(lang, "position.label")} value={data.position}
                sub={data.position === 1 ? t(lang, "position.next") : t(lang, "position.ahead", { n: data.position - 1 })}
                highlight={data.position <= 3} large={modeCfg.fontSize !== "72px"} />
            )}
            {data.estimated_wait != null && data.status === "waiting" && (
              <InfoBox icon="⏱" label={t(lang, "wait.label")} value={`${data.estimated_wait}`}
                sub={t(lang, "wait.minutes")} large={modeCfg.fontSize !== "72px"} />
            )}
            {data.status === "serving" && (
              <InfoBox icon="🔔" label={t(lang, "serving.status")} value={t(lang, "serving.now")} sub={t(lang, "serving.beingServed")} highlight large={modeCfg.fontSize !== "72px"} />
            )}
          </div>
        )}

        {/* Current station */}
        {data.current_station && (
          <div style={T.stationBox}>
            <div style={T.stationLabel}>
              {data.status === "serving" ? t(lang, "station.beingServedAt") : t(lang, "station.currentlyAt")}
            </div>
            <div style={{ ...T.stationName, fontSize: modeCfg.fontSize !== "72px" ? "22px" : "18px" }}>
              {data.current_station}
            </div>
            {data.current_counter && (
              <div style={T.counterName}>{t(lang, "station.counter", { name: data.current_counter })}</div>
            )}
          </div>
        )}

        {/* Two-way conversation: staff messages (already localized
            server-side to this ticket's own language) merged with the
            patient's own replies, plus a box to send a new reply — shown
            for any non-final status so a patient can reach staff even
            before they've received a message themselves. */}
        {!["completed", "abandoned", "expired"].includes(data.status) && (
          <ConversationSection token={token} lang={lang} messages={data.messages} onSent={() => poll(false)} />
        )}

        {/* Mobility assistance note */}
        {mode === "mobility" && data.status === "waiting" && (
          <div style={T.mobilityNote}>
            <div style={{ fontSize: "20px", marginBottom: "6px" }}>♿</div>
            <div style={{ fontSize: "14px", fontWeight: "600", color: "#60A5FA" }}>
              {t(lang, "mobility.title")}
            </div>
            <div style={{ fontSize: "13px", color: "#64748B", marginTop: "4px", lineHeight: 1.5 }}>
              {t(lang, "mobility.body")}
            </div>
          </div>
        )}

        {/* Communication assistance note */}
        {mode === "communication" && (
          <div style={T.commNote}>
            <div style={{ fontSize: "20px", marginBottom: "6px" }}>💬</div>
            <div style={{ fontSize: "14px", fontWeight: "600", color: "#A78BFA" }}>
              {t(lang, "comm.title")}
            </div>
            <div style={{ fontSize: "13px", color: "#64748B", marginTop: "4px", lineHeight: 1.5 }}>
              {t(lang, "comm.body")}
            </div>
          </div>
        )}

        {/* Journey trail */}
        {data.journey?.length > 0 && (
          <div style={T.journeySection}>
            <div style={T.journeyTitle}>{t(lang, "journey.title")}</div>
            <div style={T.journey}>
              {data.journey.map((j, i) => (
                <div key={i} style={T.journeyStep}>
                  <div style={T.journeyLeft}>
                    <div style={{
                      ...T.journeyDot,
                      background: j.status === "completed" ? "#22C55E" :
                                  j.status === "serving"   ? "#2563EB" : "#334155",
                      boxShadow: j.status === "serving" ? "0 0 0 4px rgba(37,99,235,0.2)" : "none",
                    }} />
                    {i < data.journey.length - 1 && <div style={T.journeyLine} />}
                  </div>
                  <div style={T.journeyContent}>
                    <div style={T.journeyStation}>{j.station_name}</div>
                    {j.outcome_label && <div style={T.journeyOutcome}>→ {j.outcome_label}</div>}
                    {j.wait_minutes != null && <div style={T.journeyMeta}>{t(lang, "journey.wait", { n: j.wait_minutes })}</div>}
                  </div>
                  <div style={{ ...T.journeyStatus, color: j.status === "completed" ? "#22C55E" : "#64748B" }}>
                    {j.status === "completed" ? t(lang, "journey.done") : j.status === "serving" ? t(lang, "journey.inProgress") : t(lang, "journey.waiting")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completion */}
        {data.status === "completed" && (
          <div style={T.completedBox}>
            <div style={{ fontSize: "32px", marginBottom: "8px" }}>🎉</div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#22C55E", marginBottom: "4px" }}>{t(lang, "completed.title")}</div>
            <div style={{ fontSize: "13px", color: "#64748B" }}>{t(lang, "completed.body")}</div>
          </div>
        )}

        {/* Rate the staff member you just saw — offered as soon as that
            station's leg completes, not just once at the very end. A
            multi-station visit can surface this more than once, and it
            appears alongside the overall facility rating below when the
            visit's very last station finishes. */}
        {data.pending_staff_rating && (
          <StaffRatingWidget
            token={token} lang={lang}
            journeyId={data.pending_staff_rating.journey_id}
            staffName={data.pending_staff_rating.staff_name}
            stationName={data.pending_staff_rating.station_name}
            onRated={() => poll(false)}
          />
        )}

        {data.status === "completed" && data.can_rate_facility && (
          <RatingWidget token={token} lang={lang} onRated={() => poll(false)} />
        )}
        {data.status === "completed" && data.facility_already_rated && (
          <div style={T.ratedThanks}>{t(lang, "rating.thanks")}</div>
        )}

        {(data.status === "abandoned" || data.status === "expired") && (
          <div style={T.abandonedBox}>
            <div style={{ fontSize: "13px", color: "#EF4444" }}>
              {data.status === "expired" ? t(lang, "abandoned.expiredMsg") : t(lang, "abandoned.msg")}
            </div>
          </div>
        )}

        {/* Live indicator */}
        {!["completed", "abandoned", "expired"].includes(data.status) && (
          <div style={T.liveBar}>
            <span style={T.liveDot} />
            <span style={{ fontSize: "12px", color: "#334155" }}>
              {t(lang, "live.text")}
            </span>
          </div>
        )}
      </div>
    </Shell>
  );
}

// ── Rating widget (shown once, right after visit completion) ──────────────────
function RatingWidget({ token, lang, onRated }) {
  const [stars,   setStars]   = useState(0);
  const [hover,   setHover]   = useState(0);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState(null);

  const submit = async () => {
    if (!stars || sending) return;
    setSending(true);
    setError(null);
    try {
      await publicQueue.submitRating(token, { stars, comment: comment.trim() });
      onRated && onRated();
    } catch {
      setError(t(lang, "rating.failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={T.ratingBox}>
      <div style={{ fontSize: "15px", fontWeight: "700", color: "#E2E8F0", marginBottom: "4px" }}>
        {t(lang, "rating.title")}
      </div>
      <div style={{ fontSize: "12px", color: "#64748B", marginBottom: "14px" }}>
        {t(lang, "rating.subtitle")}
      </div>
      <div style={{ display: "flex", gap: "6px", justifyContent: "center", marginBottom: "14px" }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            onClick={() => setStars(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "32px", padding: "2px", lineHeight: 1 }}
          >
            {(hover || stars) >= n ? "⭐" : "☆"}
          </button>
        ))}
      </div>
      <textarea
        style={T.ratingComment}
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder={t(lang, "rating.commentPlaceholder")}
        maxLength={500}
        rows={2}
        dir={getDir(lang)}
      />
      <button
        style={{ ...T.replySend, width: "100%", marginTop: "10px", opacity: sending || !stars ? 0.5 : 1 }}
        onClick={submit}
        disabled={sending || !stars}
      >
        {t(lang, "rating.submit")}
      </button>
      {error && <div style={T.replyError}>{error}</div>}
    </div>
  );
}

// ── Staff rating widget (shown per station, as soon as that leg completes) ────
function StaffRatingWidget({ token, lang, journeyId, staffName, stationName, onRated }) {
  const [stars,   setStars]   = useState(0);
  const [hover,   setHover]   = useState(0);
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState(null);
  const [done,    setDone]    = useState(false);

  // A fresh journeyId means a new leg completed — reset so the widget is
  // ready to rate the new leg rather than showing "already submitted"
  // from whichever leg was rated before it.
  useEffect(() => { setStars(0); setHover(0); setError(null); setDone(false); }, [journeyId]);

  const submit = async () => {
    if (!stars || sending) return;
    setSending(true);
    setError(null);
    try {
      await publicQueue.submitStaffRating(token, { journey_id: journeyId, stars });
      setDone(true);
      onRated && onRated();
    } catch {
      setError(t(lang, "staffRating.failed"));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return <div style={T.ratedThanks}>{t(lang, "staffRating.thanks", { name: staffName })}</div>;
  }

  return (
    <div style={T.ratingBox}>
      <div style={{ fontSize: "15px", fontWeight: "700", color: "#E2E8F0", marginBottom: "4px" }}>
        {t(lang, "staffRating.title", { name: staffName, station: stationName })}
      </div>
      <div style={{ fontSize: "12px", color: "#64748B", marginBottom: "14px" }}>
        {t(lang, "staffRating.subtitle")}
      </div>
      <div style={{ display: "flex", gap: "6px", justifyContent: "center", marginBottom: "14px" }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            onClick={() => setStars(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "32px", padding: "2px", lineHeight: 1 }}
          >
            {(hover || stars) >= n ? "⭐" : "☆"}
          </button>
        ))}
      </div>
      <button
        style={{ ...T.replySend, width: "100%", opacity: sending || !stars ? 0.5 : 1 }}
        onClick={submit}
        disabled={sending || !stars}
      >
        {t(lang, "staffRating.submit")}
      </button>
      {error && <div style={T.replyError}>{error}</div>}
    </div>
  );
}

// ── Conversation (two-way messages + reply box) ───────────────────────────────
function ConversationSection({ token, lang, messages, onSent }) {
  const [text,    setText]    = useState("");
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState(null);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setError(null);
    try {
      await publicQueue.sendReply(token, trimmed);
      setText("");
      onSent && onSent();
    } catch {
      setError(t(lang, "reply.failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={T.messagesSection}>
      <div style={T.journeyTitle}>{t(lang, "messages.title")}</div>

      {messages?.length > 0 && (
        <div style={T.threadList}>
          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                ...T.messageBubble,
                alignSelf: m.from === "patient" ? "flex-end" : "flex-start",
                background: m.from === "patient" ? "rgba(52,211,153,0.1)" : "rgba(37,99,235,0.1)",
                border: `1px solid ${m.from === "patient" ? "rgba(52,211,153,0.25)" : "rgba(37,99,235,0.25)"}`,
              }}
            >
              {m.text}
            </div>
          ))}
        </div>
      )}

      <div style={T.replyRow}>
        <input
          style={T.replyInput}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") handleSend(); }}
          placeholder={t(lang, "reply.placeholder")}
          maxLength={500}
          dir={getDir(lang)}
        />
        <button
          style={{ ...T.replySend, opacity: sending || !text.trim() ? 0.5 : 1 }}
          onClick={handleSend}
          disabled={sending || !text.trim()}
        >
          {t(lang, "reply.send")}
        </button>
      </div>
      {error && <div style={T.replyError}>{error}</div>}
    </div>
  );
}

// ── Accessibility mode banner ─────────────────────────────────────────────────
function AccessModeBanner({ mode, lang }) {
  const config = {
    simple:        { icon: "📖", key: "accessMode.simple",        color: "#60A5FA", bg: "rgba(37,99,235,0.1)"  },
    hearing:       { icon: "👂", key: "accessMode.hearing",       color: "#A78BFA", bg: "rgba(124,58,237,0.1)" },
    visual:        { icon: "👁",  key: "accessMode.visual",        color: "#34D399", bg: "rgba(52,211,153,0.1)" },
    mobility:      { icon: "♿", key: "accessMode.mobility",      color: "#FBBF24", bg: "rgba(251,191,36,0.1)" },
    communication: { icon: "💬", key: "accessMode.communication", color: "#F472B6", bg: "rgba(244,114,182,0.1)" },
  }[mode];
  if (!config) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px",
      padding: "10px 16px", borderRadius: "10px",
      background: config.bg, margin: "0 20px",
    }}>
      <span style={{ fontSize: "20px" }}>{config.icon}</span>
      <span style={{ fontSize: "13px", fontWeight: "600", color: config.color }}>{t(lang, config.key)}</span>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function getStatusConfig(status, lang) {
  const map = {
    waiting:     { icon: "⏳", labelKey: "status.waiting.label",     descKey: "status.waiting.desc",     color: "#EAB308", bg: "rgba(234,179,8,0.08)",   border: "rgba(234,179,8,0.2)" },
    serving:     { icon: "🔔", labelKey: "status.serving.label",     descKey: "status.serving.desc",     color: "#2563EB", bg: "rgba(37,99,235,0.1)",    border: "rgba(37,99,235,0.3)" },
    transferred: { icon: "➡️", labelKey: "status.transferred.label", descKey: "status.transferred.desc", color: "#A78BFA", bg: "rgba(124,58,237,0.08)",  border: "rgba(124,58,237,0.2)" },
    completed:   { icon: "✅", labelKey: "status.completed.label",   descKey: "status.completed.desc",   color: "#22C55E", bg: "rgba(34,197,94,0.08)",   border: "rgba(34,197,94,0.2)" },
    abandoned:   { icon: "❌", labelKey: "status.abandoned.label",   descKey: "status.abandoned.desc",   color: "#EF4444", bg: "rgba(239,68,68,0.08)",   border: "rgba(239,68,68,0.2)" },
    expired:     { icon: "🕐", labelKey: "status.expired.label",     descKey: "status.expired.desc",     color: "#64748B", bg: "rgba(100,116,139,0.08)", border: "rgba(100,116,139,0.2)" },
  };
  const cfg = map[status] || map.waiting;
  return { ...cfg, label: t(lang, cfg.labelKey), desc: t(lang, cfg.descKey) };
}

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function InfoBox({ icon, label, value, sub, highlight, large }) {
  return (
    <div style={{
      flex: 1, textAlign: "center", padding: large ? "20px 14px" : "16px 12px",
      background: highlight ? "rgba(37,99,235,0.1)" : "rgba(30,41,59,0.5)",
      border: `1px solid ${highlight ? "rgba(37,99,235,0.3)" : "rgba(51,65,85,0.4)"}`,
      borderRadius: "12px",
    }}>
      <div style={{ fontSize: "20px", marginBottom: "4px" }}>{icon}</div>
      <div style={{ fontSize: "11px", color: "#64748B", fontWeight: "600", letterSpacing: "0.06em", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: large ? "40px" : "28px", fontWeight: "800", color: highlight ? "#60A5FA" : "#E2E8F0", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: "11px", color: "#64748B", marginTop: "4px" }}>{sub}</div>
    </div>
  );
}

function LoadingView({ lang }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ width: "40px", height: "40px", border: "3px solid rgba(37,99,235,0.2)", borderTopColor: "#2563EB", borderRadius: "50%", animation: "eq-spin 0.7s linear infinite", margin: "0 auto 16px" }} />
      <div style={{ color: "#64748B", fontSize: "15px" }}>{t(lang, "loading")}</div>
    </div>
  );
}

function ErrorView({ lang, message }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 24px" }}>
      <div style={{ fontSize: "44px", marginBottom: "16px" }}>🔍</div>
      <div style={{ fontSize: "18px", fontWeight: "700", color: "#E2E8F0", marginBottom: "8px" }}>{t(lang, "errorTitle")}</div>
      <div style={{ fontSize: "14px", color: "#64748B", lineHeight: "1.6" }}>{message}</div>
    </div>
  );
}

function Shell({ children, flash, mode, lang }) {
  return (
    <div dir={getDir(lang)} style={{
      minHeight: "100vh",
      background: flash ? "#1a0a00" : (mode === "simple" || mode === "hearing" || mode === "visual") ? "#020810" : "#060D1F",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
      padding: "32px 16px",
      transition: "background 0.3s",
      animation: flash ? "eq-flash 0.5s ease-in-out 4" : "none",
    }}>
      <div style={{ width: "100%", maxWidth: "480px" }}>{children}</div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const T = {
  urgentBanner: {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
    background: "linear-gradient(135deg,#1D4ED8,#7C3AED)",
    padding: "20px 24px", textAlign: "center",
    boxShadow: "0 4px 30px rgba(37,99,235,0.5)",
  },
  urgentTitle: { fontSize: "28px", fontWeight: "900", color: "#fff", letterSpacing: "-0.5px" },
  urgentSub:   { fontSize: "16px", color: "rgba(255,255,255,0.85)", marginTop: "4px" },

  card: {
    background: "rgba(15,23,42,0.9)", border: "1px solid rgba(37,99,235,0.2)",
    borderRadius: "20px", overflow: "hidden",
    boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
  },
  cardHighContrast: {
    background: "#000", border: "2px solid #fff",
    boxShadow: "0 0 0 2px #fff",
  },
  cardHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "14px 20px", borderBottom: "1px solid rgba(37,99,235,0.1)",
    background: "rgba(37,99,235,0.05)",
  },
  poweredBy:   { fontSize: "12px", fontWeight: "700", color: "#2563EB", letterSpacing: "0.05em" },
  lastUpdated: { fontSize: "11px", color: "#334155" },
  statusBanner: {
    display: "flex", alignItems: "center", gap: "14px",
    margin: "20px 20px 0", borderRadius: "12px", padding: "14px 18px",
  },
  statusLabel: { fontSize: "16px", fontWeight: "700" },
  ticketBlock: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
    padding: "24px 20px 20px", textAlign: "center",
  },
  ticketNumLabel: { fontSize: "11px", fontWeight: "700", color: "#475569", letterSpacing: "0.1em", textTransform: "uppercase" },
  ticketNum:      { fontWeight: "900", color: "#fff", letterSpacing: "-2px", lineHeight: 1, fontVariantNumeric: "tabular-nums" },
  patientName:    { fontSize: "18px", fontWeight: "600", color: "#94A3B8" },
  priorityChip:   { borderRadius: "8px", padding: "4px 12px", fontSize: "13px", fontWeight: "700" },
  infoRow:        { display: "flex", gap: "10px", padding: "0 20px 16px" },
  stationBox: {
    margin: "0 20px 16px", background: "rgba(30,41,59,0.6)",
    border: "1px solid rgba(37,99,235,0.15)", borderRadius: "12px",
    padding: "14px 18px", textAlign: "center",
  },
  stationLabel: { fontSize: "11px", fontWeight: "600", color: "#475569", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "4px" },
  stationName:  { fontSize: "18px", fontWeight: "700", color: "#E2E8F0" },
  counterName:  { fontSize: "13px", color: "#64748B", marginTop: "3px" },
  ratingBox: {
    margin: "0 20px 16px", background: "rgba(37,99,235,0.06)",
    border: "1px solid rgba(37,99,235,0.2)", borderRadius: "14px",
    padding: "18px", textAlign: "center",
  },
  ratingComment: {
    width: "100%", boxSizing: "border-box", resize: "none",
    background: "rgba(30,41,59,0.6)", border: "1px solid rgba(51,65,85,0.5)",
    borderRadius: "10px", padding: "10px 12px", color: "#E2E8F0",
    fontSize: "13px", fontFamily: "inherit", outline: "none",
  },
  ratedThanks: {
    margin: "0 20px 16px", background: "rgba(34,197,94,0.08)",
    border: "1px solid rgba(34,197,94,0.2)", borderRadius: "12px",
    padding: "14px", textAlign: "center", fontSize: "13px", color: "#22C55E", fontWeight: "600",
  },
  messagesSection: { padding: "0 20px 16px" },
  threadList: { display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px", maxHeight: "220px", overflowY: "auto" },
  messageBubble: {
    display: "flex", maxWidth: "82%",
    borderRadius: "12px", padding: "10px 14px", fontSize: "13px",
    color: "#E2E8F0", lineHeight: 1.5,
  },
  replyRow: { display: "flex", gap: "8px" },
  replyInput: {
    flex: 1, background: "rgba(30,41,59,0.6)", border: "1px solid rgba(51,65,85,0.5)",
    borderRadius: "10px", padding: "10px 14px", color: "#E2E8F0", fontSize: "13px",
    outline: "none", fontFamily: "inherit",
  },
  replySend: {
    padding: "10px 18px", borderRadius: "10px", border: "none",
    background: "#2563EB", color: "#fff", fontSize: "13px", fontWeight: "700",
    cursor: "pointer", fontFamily: "inherit",
  },
  replyError: { fontSize: "12px", color: "#EF4444", marginTop: "8px" },
  mobilityNote: {
    margin: "0 20px 16px", background: "rgba(251,191,36,0.07)",
    border: "1px solid rgba(251,191,36,0.2)", borderRadius: "12px",
    padding: "16px 18px", textAlign: "center",
  },
  commNote: {
    margin: "0 20px 16px", background: "rgba(167,139,250,0.07)",
    border: "1px solid rgba(167,139,250,0.2)", borderRadius: "12px",
    padding: "16px 18px", textAlign: "center",
  },
  journeySection: { padding: "0 20px 16px" },
  journeyTitle:   { fontSize: "11px", fontWeight: "700", color: "#475569", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "12px" },
  journey:        { display: "flex", flexDirection: "column" },
  journeyStep:    { display: "flex", gap: "12px", paddingBottom: "4px" },
  journeyLeft:    { display: "flex", flexDirection: "column", alignItems: "center", width: "16px", flexShrink: 0 },
  journeyDot:     { width: "14px", height: "14px", borderRadius: "50%", flexShrink: 0, marginTop: "3px", transition: "all 0.3s" },
  journeyLine:    { width: "2px", flex: 1, background: "rgba(37,99,235,0.15)", margin: "4px 0", minHeight: "20px" },
  journeyContent: { flex: 1, paddingBottom: "14px", minWidth: 0 },
  journeyStation: { fontSize: "14px", fontWeight: "600", color: "#E2E8F0" },
  journeyOutcome: { fontSize: "12px", color: "#64748B", marginTop: "2px" },
  journeyMeta:    { fontSize: "11px", color: "#334155", marginTop: "2px" },
  journeyStatus:  { fontSize: "11px", fontWeight: "600", flexShrink: 0, paddingTop: "4px" },
  completedBox: {
    margin: "0 20px 16px", background: "rgba(34,197,94,0.08)",
    border: "1px solid rgba(34,197,94,0.2)", borderRadius: "12px",
    padding: "24px", textAlign: "center",
  },
  abandonedBox: {
    margin: "0 20px 16px", background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.2)", borderRadius: "12px",
    padding: "14px 18px",
  },
  liveBar: {
    display: "flex", alignItems: "center", gap: "8px", justifyContent: "center",
    padding: "12px 20px", borderTop: "1px solid rgba(37,99,235,0.08)",
    background: "rgba(37,99,235,0.03)",
  },
  liveDot: {
    width: "7px", height: "7px", borderRadius: "50%",
    background: "#22C55E", boxShadow: "0 0 6px #22C55E",
    flexShrink: 0, display: "inline-block",
    animation: "eq-pulse 2s ease-in-out infinite",
  },
};
